import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Media from 'react-media';

import GLOBAL_MEDIA_QUERIES from 'utils/facility';
import { getPageOnHostByID, performMutation } from 'lib/api';
import { requireQueryByName } from 'lib/dependencies';
import trackAction from 'utils/tracking';
import { decodeFilterData, encodeFilterData } from 'helpers/filter';

import getActivitiesQuery from 'mutations/getActivities';

import ActivityCard from 'components/ActivityCard';
import Filter from 'components/Filter';
import Loading from 'components/Core/Loading';

import { ActivityCard as ActivityCardElem, CountElem } from './ActivityIndex.styles';

const ActivityIndex = ({
  page,
  activities,
  filterSetsGeneric,
  filterSetsTheme,
  themes,
  filterThemeId,
  activeFiltersGeneric,
  activeFiltersTheme,
  showWizard,
}) => {
  const router = useRouter();
  const [hydrated, setHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activitiesList, setActivitiesList] = useState(activities);
  const [resultCount, setResultCount] = useState(activities.length);
  const [themeId, setThemeId] = useState(filterThemeId);
  const [activeFiltersG, setActiveFiltersG] = useState(activeFiltersGeneric);
  const [activeFiltersT, setActiveFiltersT] = useState(activeFiltersTheme);
  const [filterSetsT, setFilterSetsT] = useState(filterSetsTheme);

  const updateUrl = (data) => {
    const pathAs = `${page.url}?theme=${themeId}&filters=${data}`;
    router.replace(
      {
        pathname: `/[...slug]?theme=${themeId}&filters=${data}`,
        query: { filters: `${data}`, theme: `${themeId}` },
      },
      pathAs,
      { shallow: true },
    );
  };

  // Parse active filters to form a proper URL
  const parseActiveFilters = (filters) => {
    const activeFilters = [];
    filters.forEach((setElem) => {
      const indexExists = activeFilters.findIndex((elem) => elem.filterSet === setElem.filterSet.slug);
      if (indexExists !== -1) {
        activeFilters[indexExists].filters.push(setElem.slug);
      } else {
        activeFilters.push({ filterSet: setElem.filterSet.slug, filters: [setElem.slug] });
      }
    });
    return activeFilters;
  };

  // This is where we set initial state of the filters based on what we get from server
  const [parsedFilterData, setParsedFilterData] = useState(() =>
    parseActiveFilters([...activeFiltersGeneric, ...activeFiltersTheme]),
  );

  const onItemClick = (itemLabel) => {
    trackAction('suggested-activity', itemLabel, 'click');
  };

  const updateResults = async () => {
    setIsLoading(true);
    await performMutation(getActivitiesQuery, {
      activityIndexId: page.id,
      filters: parsedFilterData,
      themeId,
    })
      .then(({ getActivities }) => {
        setActivitiesList(getActivities.activities);
        setResultCount(getActivities.resultCount);
        setActiveFiltersG(getActivities.activeFiltersGeneric);
        setActiveFiltersT(getActivities.activeFiltersTheme);
        setFilterSetsT(getActivities.themeFilters);

        // update URL with active filters
        updateUrl(
          encodeFilterData(
            parseActiveFilters([...getActivities.activeFiltersGeneric, ...getActivities.activeFiltersTheme]),
          ),
        );
      })
      .then(() => setIsLoading(false));
  };

  // Used only so that we can skip updating the results on initial render
  useEffect(() => {
    setHydrated(true);
  }, []);

  // Update results after theme or filters update
  useEffect(() => {
    if (hydrated) {
      updateResults();
    }
  }, [themeId, parsedFilterData]);

  const onThemeChange = (id) => {
    setThemeId(id);
  };

  const onFilterChange = (category, filter) => {
    const tempFilterData = JSON.parse(JSON.stringify(parsedFilterData));
    const categoryIndex = tempFilterData.findIndex((item) => item.filterSet === category);

    if (categoryIndex !== -1) {
      const filterIndex = tempFilterData[categoryIndex].filters.findIndex((elem) => elem === filter);
      if (filterIndex !== -1) {
        if (tempFilterData[categoryIndex].filters.length > 1) {
          tempFilterData[categoryIndex].filters.splice(filterIndex, 1);
        } else {
          tempFilterData.splice(categoryIndex, 1);
        }
      } else {
        tempFilterData[categoryIndex].filters.push(filter);
      }
    } else {
      tempFilterData.push({ filterSet: category, filters: [filter] });
    }

    setParsedFilterData(tempFilterData);
  };

  const resetFilters = () => {
    setThemeId(null);
    setParsedFilterData([]);
  };

  useEffect(() => {
    const compareAndReset = (url) => {
      if (url === page.url) {
        resetFilters();
      }
    };
    router.events.on('routeChangeComplete', compareAndReset);
    return () => {
      router.events.off('routeChangeComplete', compareAndReset);
    };
  }, []);

  const getActiveTheme = (thId) => {
    const matchedTheme = themes.find((th) => th.id === thId);
    return matchedTheme || null;
  };

  return (
    <div className="container mx-auto px-4 sm:px-0 u-block-margins">
      {filterSetsGeneric && showWizard && (
        <Filter
          filterSetsGeneric={filterSetsGeneric}
          filterSetsTheme={filterSetsT}
          activeFiltersGeneric={activeFiltersG}
          activeFiltersTheme={activeFiltersT}
          themes={themes}
          activeTheme={themeId ? getActiveTheme(themeId) : null}
          onThemeChange={onThemeChange}
          onFilterChange={onFilterChange}
          resultCount={resultCount}
          initialFilterData={parsedFilterData}
          resetFilters={resetFilters}
          isLoading={isLoading}
        />
      )}
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <div className="flex justify-center items-center my-4 md:my-8">
            <Media queries={GLOBAL_MEDIA_QUERIES} defaultMatches={{ sm: true }}>
              {(matches) =>
                matches.sm || matches.xs ? (
                  <h5>
                    <CountElem>{activitiesList.length}</CountElem>Activiteiten gevonden voor jou
                  </h5>
                ) : (
                  <h2>
                    <CountElem>{activitiesList.length}</CountElem>Activiteiten gevonden voor jou
                  </h2>
                )
              }
            </Media>
          </div>
          <div className="flex flex-wrap">
            {activitiesList.map((activity) => {
              // ActivityPage contains the activity data in the key `activity` and
              // CustomActivityPage doesn't.
              const activityData = activity.activity ? activity.activity : activity;
              const activityUrl = activity.url;
              return (
                <Link key={activityData.slug} href="/[...slug]" as={activityUrl} passHref>
                  <ActivityCardElem className="px-3 my-3 group" onClick={() => onItemClick(activityData.title)}>
                    <ActivityCard {...{ ...activityData, url: activityUrl }} />
                  </ActivityCardElem>
                </Link>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// The data returned here will be send as `props` to the component
// TODO: Let's see if we need this here or can ultimately adopt this into the lib/api calls directly
// That will depend on how different these calls are going to be along the way.
ActivityIndex.getCustomInitialProps = async ({
  id,
  contentType,
  hostname,
  previewData,
  filterData,
  filterThemeId,
}) => {
  const query = requireQueryByName('ActivityIndex');
  const token = previewData ? previewData.token : '';
  const {
    page,
    activities,
    filterSetsGeneric,
    filterSetsTheme,
    themes,
    activeFilters: { activeFiltersGeneric, activeFiltersTheme },
  } = await getPageOnHostByID({
    id,
    contentType,
    query,
    token,
    hostname,
    filters: decodeFilterData(filterData),
    filterThemeId,
  });

  return {
    page,
    extraPageProps: {
      activities,
      filterSetsGeneric,
      filterSetsTheme,
      activeFiltersGeneric,
      activeFiltersTheme,
      themes,
      filterData,
      filterThemeId,
    },
  };
};

ActivityIndex.defaultProps = {
  page: {
    title: '',
    url: '',
  },
  filterSetsGeneric: null,
  filterSetsTheme: null,
  activeFiltersGeneric: null,
  activeFiltersTheme: null,
  filterThemeId: null,
  themes: null,
};

ActivityIndex.propTypes = {
  page: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    url: PropTypes.string,
  }),
  showWizard: PropTypes.bool.isRequired,
  activities: PropTypes.arrayOf(ActivityCard).isRequired,
  filterSetsGeneric: PropTypes.arrayOf(Object),
  filterSetsTheme: PropTypes.arrayOf(Object),
  activeFiltersGeneric: PropTypes.arrayOf(Object),
  activeFiltersTheme: PropTypes.arrayOf(Object),
  filterThemeId: PropTypes.string,
  themes: PropTypes.arrayOf(Object),
};

export default ActivityIndex;
